import { useRef, useState } from 'react'
import { zipFolder, ZIP_WARN_BYTES } from '../utils/zipFolder'
import { detectInputTypeFromFileName, detectInputTypeFromFiles, recommendOutputType, supportsQuantize } from '../utils/formatMapping'
import { dataApi } from '../api/client'

const CRS_OPTIONS = [
  { code: 5186, label: 'EPSG:5186 — Korea 2000 Central (TM 중부원점)' },
  { code: 5179, label: 'EPSG:5179 — Korea 2000 / UTM-K (GRS80)' },
  { code: 5174, label: 'EPSG:5174 — Korean 1985 Central (Bessel 중부)' },
  { code: 5187, label: 'EPSG:5187 — Korea 2000 East (TM 동부원점)' },
  { code: 32652, label: 'EPSG:32652 — UTM Zone 52N' },
  { code: 3857, label: 'EPSG:3857 — Web Mercator' },
  { code: 4326, label: 'EPSG:4326 — WGS 84 (위경도)' },
]

const CUSTOM_CRS = 'custom'

const ACCEPTED_EXTS = ['.obj', '.ply', '.fbx', '.3ds', '.las', '.laz', '.gltf', '.glb', '.dae', '.ifc', '.gml', '.shp', '.geojson', '.gpkg', '.kml', '.zip']

interface Props {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  onSwitchToAdvanced: () => void
}

export default function SimpleRegisterModal({ open, onClose, onSuccess, onSwitchToAdvanced }: Props) {
  const fileRef   = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement | null>(null)

  const [uploadMode,  setUploadMode]  = useState<'file' | 'folder'>('file')
  const [file,        setFile]        = useState<File | null>(null)
  const [folderFiles, setFolderFiles] = useState<File[] | null>(null)
  const [folderName,  setFolderName]  = useState('')
  const [isDragging,  setIsDragging]  = useState(false)
  const [name,        setName]        = useState('')
  const [description, setDescription] = useState('')
  const [crs,         setCrs]         = useState<number | typeof CUSTOM_CRS>(5186)
  const [customCrs,   setCustomCrs]   = useState('')
  const [fileError,   setFileError]   = useState('')
  const [error,       setError]       = useState('')
  const [uploadPct,   setUploadPct]   = useState<number | null>(null)
  const [zipping,     setZipping]     = useState(false)
  const [zipPct,      setZipPct]      = useState(0)

  if (!open) return null

  const isBusy       = zipping || (uploadPct !== null && uploadPct < 100)
  const hasInput     = uploadMode === 'file' ? !!file : (folderFiles !== null && folderFiles.length > 0)
  const crsIsCustom  = String(crs) === CUSTOM_CRS
  const crsValid     = !crsIsCustom || /^\d+$/.test(customCrs.trim())
  const canSubmit    = hasInput && name.trim().length > 0 && crsValid && !isBusy
  const detectedInputType = uploadMode === 'file'
    ? (file ? detectInputTypeFromFileName(file.name) : 'auto')
    : (folderFiles ? detectInputTypeFromFiles(folderFiles) : 'auto')
  const outputType   = recommendOutputType(detectedInputType)
  const quantize     = supportsQuantize(outputType)

  const pickFile = (f: File) => {
    if (!ACCEPTED_EXTS.some(ext => f.name.toLowerCase().endsWith(ext))) {
      setFileError('지원하지 않는 파일 형식입니다.'); return
    }
    setFile(f); setFileError('')
  }

  const pickFolder = (files: FileList) => {
    if (files.length === 0) return
    const arr = Array.from(files)
    const top = arr[0].webkitRelativePath.split('/')[0] || '폴더'
    setFolderFiles(arr)
    setFolderName(top)
    if (!name) setName(top)
    setFileError('')
  }

  const reset = () => {
    setUploadMode('file')
    setFile(null); setFolderFiles(null); setFolderName('')
    setName(''); setDescription(''); setCrs(5186); setCustomCrs('')
    setFileError(''); setError(''); setUploadPct(null); setZipping(false); setZipPct(0)
  }
  const handleClose = () => { reset(); onClose() }

  const handleSubmit = async () => {
    if (!name.trim() || !crsValid) return
    setError('')

    let uploadFile: File
    if (uploadMode === 'folder') {
      if (!folderFiles || folderFiles.length === 0) return
      const totalSize = folderFiles.reduce((s, f) => s + f.size, 0)
      if (totalSize > ZIP_WARN_BYTES) {
        setError(`폴더 크기(${(totalSize / 1e9).toFixed(1)} GB)가 2 GB를 초과합니다. 미리 ZIP으로 압축한 뒤 파일 업로드를 이용하면 더 안정적입니다.`)
      }
      setZipping(true); setZipPct(0)
      try {
        const blob = await zipFolder(folderFiles, setZipPct)
        uploadFile = new File([blob], `${folderName}.zip`, { type: 'application/zip' })
      } catch {
        setError('폴더 압축에 실패했습니다. 다시 시도해주세요.')
        setZipping(false)
        return
      }
      setZipping(false)
    } else {
      if (!file) return
      uploadFile = file
    }

    setUploadPct(0)
    const fd = new FormData()
    fd.append('file', uploadFile)
    fd.append('name', name.trim())
    if (description.trim()) fd.append('description', description.trim())
    fd.append('crs', crsIsCustom ? customCrs.trim() : String(crs))
    fd.append('xOffset', '0'); fd.append('yOffset', '0'); fd.append('zOffset', '0')
    fd.append('outputType', outputType)
    fd.append('tilesVersion', '1.1')
    fd.append('quantize', String(quantize))
    try {
      await dataApi.upload(fd, setUploadPct)
      reset(); onSuccess()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '업로드에 실패했습니다.')
      setUploadPct(null)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
      onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[560px] flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 rounded-t-xl shrink-0"
          style={{ background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-white shrink-0">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-white">간편 등록</h2>
            <p className="text-[11px] text-white/80">파일과 기본 정보만 입력하면 자동으로 변환됩니다</p>
          </div>
          {!isBusy && (
            <button onClick={handleClose} className="bg-white/20 hover:bg-white/30 text-white rounded p-1.5 transition text-sm leading-none">✕</button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm">{error}</div>
          )}

          {/* 업로드 방식 토글 */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1.5">
              업로드 방식 <span className="text-red-500">*</span>
            </label>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => { if (!isBusy) { setUploadMode('file'); setFolderFiles(null); setFolderName(''); setFileError('') } }}
                className={`flex-1 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                  uploadMode === 'file' ? 'bg-green-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                파일 업로드
              </button>
              <button
                type="button"
                onClick={() => { if (!isBusy) { setUploadMode('folder'); setFile(null); setFileError('') } }}
                className={`flex-1 py-2 text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                  uploadMode === 'folder' ? 'bg-green-600 text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                </svg>
                폴더 업로드
              </button>
            </div>
          </div>

          {/* 파일 / 폴더 선택 영역 */}
          {uploadMode === 'file' ? (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1.5">
                3D Mesh 파일 <span className="text-red-500">*</span>
              </label>
              <div
                onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={e => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) pickFile(f) }}
                onClick={() => !isBusy && fileRef.current?.click()}
                className={[
                  'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                  isDragging ? 'border-green-400 bg-green-50' : 'border-slate-200 bg-slate-50 hover:border-slate-300',
                  isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                ].join(' ')}
              >
                <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 mx-auto mb-2 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                  </svg>
                </div>
                {file ? (
                  <p className="text-sm font-semibold text-slate-700">{file.name}</p>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-700">파일을 드래그하거나 <span className="text-green-600">클릭하여 선택</span></p>
                    <p className="text-[10px] text-slate-400 mt-1">OBJ · FBX · GLTF · GLB · DAE · LAS · LAZ · CityGML · ZIP 등</p>
                  </>
                )}
                <input ref={fileRef} type="file" className="sr-only"
                  accept=".obj,.ply,.fbx,.las,.laz,.gltf,.glb,.dae,.ifc,.gml,.shp,.geojson,.gpkg,.kml,.zip"
                  onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = '' }}
                  disabled={isBusy}/>
              </div>
              {fileError && <p className="text-xs text-red-600 mt-1">{fileError}</p>}
            </div>
          ) : (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1.5">
                3D Mesh 폴더 <span className="text-red-500">*</span>
              </label>
              <div
                onClick={() => !isBusy && folderRef.current?.click()}
                className={[
                  'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                  'border-slate-200 bg-slate-50 hover:border-slate-300',
                  isBusy ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                ].join(' ')}
              >
                <div className="w-10 h-10 rounded-lg bg-green-100 text-green-600 mx-auto mb-2 flex items-center justify-center">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                  </svg>
                </div>
                {folderFiles ? (
                  <>
                    <p className="text-sm font-semibold text-slate-700">{folderName}</p>
                    <p className="text-xs text-slate-400 mt-1">{folderFiles.length}개 파일 선택됨</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold text-slate-700">클릭하여 <span className="text-green-600">폴더 선택</span></p>
                    <p className="text-[10px] text-slate-400 mt-1">폴더 내 모든 파일이 ZIP으로 묶여 업로드됩니다</p>
                  </>
                )}
                <input
                  ref={(el) => {
                    if (el) el.webkitdirectory = true
                    folderRef.current = el
                  }}
                  type="file"
                  className="sr-only"
                  multiple
                  onChange={e => { if (e.target.files) pickFolder(e.target.files); e.target.value = '' }}
                  disabled={isBusy}
                />
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              제목 <span className="text-red-500">*</span>
            </label>
            <input value={name} onChange={e => setName(e.target.value)}
              placeholder="예: 강남구 빌딩 메시" className="input-field" disabled={isBusy}/>
          </div>

          {/* Description */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">설명</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="데이터에 대한 간단한 설명 (선택)" rows={2}
              className="input-field resize-none" disabled={isBusy}/>
          </div>

          {/* CRS */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">
              좌표계 <span className="text-[10px] font-mono text-slate-400 ml-1">-crs</span>
            </label>
            <select
              value={crsIsCustom ? CUSTOM_CRS : crs}
              onChange={e => setCrs(e.target.value === CUSTOM_CRS ? CUSTOM_CRS : Number(e.target.value))}
              className="input-field" disabled={isBusy}>
              {CRS_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
              <option value={CUSTOM_CRS}>직접 입력 (EPSG 코드)</option>
            </select>
            {crsIsCustom && (
              <div className="mt-2">
                <input
                  type="text" inputMode="numeric" pattern="[0-9]*"
                  value={customCrs}
                  onChange={e => setCustomCrs(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="EPSG 코드 입력 (예: 5181)"
                  className="input-field" disabled={isBusy}
                />
                {customCrs.trim().length > 0 && !crsValid && (
                  <p className="text-xs text-red-600 mt-1">숫자로 된 EPSG 코드를 입력해주세요.</p>
                )}
              </div>
            )}
          </div>

          {/* Auto-applied info */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2 text-[11px] text-green-700">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mt-0.5 shrink-0">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <div>
              <strong className="block mb-1">자동 적용되는 옵션</strong>
              <span className="text-green-800 leading-relaxed">
                출력 포맷: <strong>{outputType.toUpperCase()}</strong>
                {' '}(업로드 파일 형식에 따라 자동 결정) · 3D Tiles 버전: <strong>v1.1</strong>
                {quantize && <> · 메시 양자화</>} · Geoid: <strong>WGS84 Ellipsoid</strong> · 입력 타입: <strong>자동 감지</strong>
              </span>
            </div>
          </div>

          <button onClick={onSwitchToAdvanced} disabled={isBusy}
            className="w-full border border-dashed border-slate-200 text-primary-600 text-[11px] font-semibold py-2 rounded-lg hover:bg-primary-50 transition flex items-center justify-center gap-2 disabled:opacity-50">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
            더 세밀하게 제어하려면 상세 옵션 등록으로 전환
          </button>

          {/* 압축 중 표시 */}
          {zipping && (
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>폴더 묶는 중... ({folderFiles?.length ?? 0}개 파일)</span>
                <span>{zipPct}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div className="bg-green-400 h-1.5 rounded-full transition-all duration-100" style={{ width: `${zipPct}%` }}/>
              </div>
            </div>
          )}

          {/* 업로드 진행 */}
          {uploadPct !== null && (
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{uploadPct < 100 ? '서버 업로드 중...' : '업로드 완료'}</span>
                <span>{uploadPct}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5">
                <div className="bg-green-500 h-1.5 rounded-full transition-all duration-200" style={{ width: `${uploadPct}%` }}/>
              </div>
              {uploadPct === 100 && (
                <p className="text-[11px] text-slate-400 mt-1">서버에서 변환 작업을 시작했습니다. 완료 후 이메일이 발송됩니다.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 rounded-b-xl flex justify-end gap-2 shrink-0">
          <button onClick={handleClose} disabled={isBusy} className="btn-secondary disabled:opacity-50">취소</button>
          <button onClick={handleSubmit} disabled={!canSubmit}
            className="px-5 py-2 rounded-md text-sm font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: canSubmit ? '#16a34a' : undefined }}>
            {zipping ? `묶는 중... ${zipPct}%` : isBusy ? '업로드 중...' : '등록 및 변환 시작'}
          </button>
        </div>
      </div>
    </div>
  )
}
